import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ThemeLogo from './ThemeLogo';
import { Link } from 'react-router-dom';
import {
  confirmResetPassword,
  resetPassword,
  signIn,
  signOut,
} from '@aws-amplify/auth';

import './stylesheet/customLogin.css';

function ForgotPassword() {
  const [step, setStep] = useState('enterEmail');
  const [resetUserName, setResetUserName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => setOpenError(false);

  const showError = (msg) => {
    setOpenError(true);
    setErrorMessage(msg);
    setLoading(false);
  };

  const handleSendCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!resetUserName.trim()) {
      showError('Please enter your email or username.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ username: resetUserName });
      setStep('enterCode');
      setLoading(false);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await resetPassword({ username: resetUserName });
      setLoading(false);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleSubmitNewPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      await confirmResetPassword({
        username: resetUserName,
        confirmationCode: code,
        newPassword,
      });
      const { isSignedIn } = await signIn({
        username: resetUserName,
        password: newPassword,
      });
      if (isSignedIn) {
        await signOut();
        window.location.href = '/';
      }
    } catch (error) {
      if (error.name === 'UserLambdaValidationException') {
        showError('This email is already being used by another account');
      } else {
        showError(error.message);
      }
    }
  };

  const handleFormSubmission = (e) => {
    if (step === 'enterEmail') {
      handleSendCode(e);
    } else {
      handleSubmitNewPassword(e);
    }
  };

  return (
    <div className="mainDiv">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openError}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
      <form onSubmit={handleFormSubmission}>
        {step === 'enterEmail' && (
          <div className="auth-stagger">
            <div className="auth-logo">
              <ThemeLogo />
            </div>
            <div>
              <h4>Reset your password</h4>
              <p className="auth-subtitle">
                Enter the email or username associated with your account and we'll send a reset code.
              </p>
            </div>
            <div className="inputArea">
              <p className="inputDescription">Email or Username</p>
              <TextField
                id="resetUserName"
                key="resetUserName"
                name="resetUserName"
                fullWidth
                placeholder="name@company.com"
                variant="outlined"
                type="text"
                size="small"
                value={resetUserName}
                onChange={(e) => setResetUserName(e.target.value)}
              />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                marginTop: 20,
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  style={{ borderRadius: 8 }}
                >
                  Back
                </Button>
              </Link>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                style={{ borderRadius: 8, minWidth: 120, position: 'relative' }}
              >
                {loading ? '' : 'Send Code'}
                {loading && <CircularProgress size={20} color="inherit" />}
              </Button>
            </div>
          </div>
        )}
        {step === 'enterCode' && (
          <div className="auth-stagger">
            <div className="auth-logo">
              <ThemeLogo />
            </div>
            <div>
              <h4>Enter reset code</h4>
              <p className="auth-subtitle">
                We sent a code to the email associated with your account. Enter it below with your new password.
              </p>
            </div>
            <div className="inputArea">
              <p className="inputDescription">Reset code</p>
              <TextField
                id="code"
                key="code"
                name="code"
                fullWidth
                placeholder="Enter the 6-digit code"
                variant="outlined"
                type="text"
                size="small"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <p className="inputDescription">New Password</p>
              <TextField
                fullWidth
                placeholder="Enter your new password"
                variant="outlined"
                id="newPassword"
                key="newPassword"
                name="newPassword"
                type="password"
                size="small"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="s-text noWrap">
                Must contain 6+ characters, special character(s), number(s), uppercase and lower
                case letter(s).
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                marginTop: 20,
              }}
            >
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  style={{ borderRadius: 8 }}
                >
                  Back
                </Button>
              </Link>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleResendCode}
                disabled={loading}
                style={{ borderRadius: 8 }}
              >
                Resend Code
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                style={{ borderRadius: 8, minWidth: 100, position: 'relative' }}
              >
                {loading ? '' : 'Submit'}
                {loading && <CircularProgress size={20} color="inherit" />}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default ForgotPassword;
